import { useEffect, useRef } from 'react';

/**
 * Tab で辿れる要素。disabled・tabindex="-1"・hidden は除く。
 *
 * 「画面に見えているか」までは見ない: offsetParent や getClientRects は
 * position: fixed の要素や jsdom で当てにならず、見えている要素を取りこぼす方が
 * 閉じ込め漏れより実害が大きい。
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
]
  .map((selector) => `${selector}:not([hidden])`)
  .join(', ');

/**
 * 開いている overlay の数。Modal の上に Drawer が重なっても、
 * 最後の 1 枚が閉じるまで body のスクロールを戻さない。
 */
let openCount = 0;
let restoreOverflow = '';

function lockScroll() {
  if (openCount === 0) {
    restoreOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  openCount += 1;
}

function unlockScroll() {
  openCount = Math.max(0, openCount - 1);
  if (openCount === 0) {
    document.body.style.overflow = restoreOverflow;
  }
}

function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
}

export interface OverlayBehaviorOptions {
  open: boolean;
  onClose: () => void;
  /** Escape で閉じない（破壊的な確認ダイアログなど） */
  closeOnEscape?: boolean;
}

/**
 * Modal / Drawer が共有する挙動:
 *   - 開いたら本文のスクロールを止め、閉じたら戻す
 *   - Escape で閉じる
 *   - Tab / Shift+Tab を中に閉じ込める
 *   - 開く前にフォーカスしていた要素へ、閉じたときに戻す
 *
 * 返り値の ref を、閉じ込めたい要素（ダイアログ本体）に付ける。
 */
export function useOverlayBehavior({ open, onClose, closeOnEscape = true }: OverlayBehaviorOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // onClose の同一性に依存して effect を貼り直すと、呼び出し側が毎描画で
  // 新しい関数を渡してきたときにスクロールロックが上下する。ref で逃がす。
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    lockScroll();

    const container = containerRef.current;
    if (container) {
      const first = focusableWithin(container)[0];
      (first ?? container).focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const node = containerRef.current;
      if (!node) return;

      const items = focusableWithin(node);
      if (items.length === 0) {
        event.preventDefault();
        node.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === node)) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      unlockScroll();
      previouslyFocused?.focus?.();
    };
  }, [open, closeOnEscape]);

  return containerRef;
}
